import { useState, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MaskedInputService from 'services/maskedInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import bankingWithdrawalAccountValidator from 'validators/bankingWithdrawalAccountValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaUniversity } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Card,
  CardHeader,
  CardTitle,
  Caption,
  Row,
  FormField,
  FieldLabel,
  FormActions,
  Divider,
  InfoText,
  WarningText,
  MaskedInput
} from '../../styles'

/**
 * O cadastro da conta bancária que recebe os saques, feito na hora de sacar.
 *
 * Mora no caminho de quem vai sacar, e não no acompanhamento da abertura, porque é só para
 * isso que ela serve: a aprovação da conta Cutilei depende dos dados comerciais e dos
 * documentos, e o Pix por chave manda o saldo para qualquer conta sem ela.
 *
 * O titular informa banco, agência, conta e tipo — nunca nome nem documento. Quem preenche
 * os dois é o Asaas, a partir da própria subconta, e é isso que mantém o saque sendo um
 * saque em vez de uma transferência para a conta de qualquer um.
 *
 * @param onRegistered - Recebe o destino do saque já com a conta cadastrada, no mesmo formato
 *   de `GET /banking_account/withdrawal_target`.
 */
export default function WithdrawalAccountForm ({
  requestConfig,
  onRegistered: handleRegistered = () => {}
}) {
  const [banks, setBanks] = useState (null)
  const [requestError, setRequestError] = useState (null)

  useEffect (() => {
    getBanks ()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getBanks = async () => {
    try {
      const { data } = await cutileiApi.get ('/banks', requestConfig)

      setBanks (data.map (bank => ({
        label: `(${bank.code}) ${bank.name}`,
        value: bank
      })))
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError ('Não foi possível carregar a lista de bancos')
    }
  }

  if (!banks && !requestError) return (
    <Card>
      <Loading style={{alignSelf: 'center'}}/>
    </Card>
  )

  return (
    <Formik
      validationSchema={bankingWithdrawalAccountValidator}
      initialValues={{
        bank: null,
        bankAccountType: null,
        agency: '',
        account: '',
        accountDigit: ''
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting (true)
          setRequestError (null)

          const { data } = await cutileiApi.post ('/banking_account/withdrawal_target', {
            bankId: values.bank.value.id,
            bankAccountType: values.bankAccountType.value,
            agency: values.agency,
            account: values.account,
            accountDigit: values.accountDigit
          }, requestConfig)

          handleRegistered (data)
        } catch (error) {
          console.log (error.response ?? error)
          setRequestError (
            error.response?.data?.message ?? 'Não foi possível cadastrar a sua conta'
          )
        } finally {
          setSubmitting (false)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        handleSubmit
      }) => (
        <Card gap={12}>
          <CardHeader>
            <CardTitle>Conta para saque</CardTitle>
            <FaUniversity size={16} color='#FF9800'/>
          </CardHeader>
          <Caption>
            {'Para sacar, cadastre a conta bancária que vai receber o dinheiro. Ela precisa '
            + 'estar no seu nome — o banco usa o mesmo CPF ou CNPJ da sua conta Cutilei'}
          </Caption>
          <Divider/>
          <Row>
            <FormField stretch={3} maxWidth={320}>
              <FieldLabel>Banco</FieldLabel>
              <Select
                name='bank'
                placeholder='Selecione'
                options={banks ?? []}
                value={values.bank}
                error={errors.bank}
                touched={touched.bank}
                onChange={setFieldValue}
                onBlur={setFieldTouched}
              />
              {touched.bank && errors.bank && <WarningText>{errors.bank}</WarningText>}
            </FormField>
            <FormField stretch={3} maxWidth={220}>
              <FieldLabel>Tipo de conta</FieldLabel>
              <Select
                name='bankAccountType'
                placeholder='Selecione'
                options={BankingService.BANK_ACCOUNT_TYPES}
                value={values.bankAccountType}
                error={errors.bankAccountType}
                touched={touched.bankAccountType}
                onChange={setFieldValue}
                onBlur={setFieldTouched}
              />
              {touched.bankAccountType && errors.bankAccountType && (
                <WarningText>{errors.bankAccountType}</WarningText>
              )}
            </FormField>
          </Row>
          <Row>
            <FormField stretch={2} maxWidth={160}>
              <FieldLabel>Agência</FieldLabel>
              {/* Sem o dígito: o Asaas não tem campo para o dígito da agência. */}
              <MaskedInput
                name='agency'
                placeholder='0000'
                options={{blocks: [6], numericOnly: true}}
                value={values.agency}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('agency')}
              />
              {touched.agency && errors.agency && <WarningText>{errors.agency}</WarningText>}
            </FormField>
            <FormField stretch={3} maxWidth={220}>
              <FieldLabel>Conta</FieldLabel>
              <MaskedInput
                name='account'
                placeholder='00000000'
                options={{blocks: [15], numericOnly: true}}
                value={values.account}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('account')}
              />
              {touched.account && errors.account && (
                <WarningText>{errors.account}</WarningText>
              )}
            </FormField>
            <FormField stretch={1} maxWidth={90}>
              <FieldLabel>Dígito</FieldLabel>
              <MaskedInput
                name='accountDigit'
                placeholder='0'
                options={{blocks: [1], uppercase: true}}
                value={values.accountDigit}
                onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                onBlur={handleBlur ('accountDigit')}
              />
              {touched.accountDigit && errors.accountDigit && (
                <WarningText>{errors.accountDigit}</WarningText>
              )}
            </FormField>
          </Row>
          {requestError && <WarningText>{requestError}</WarningText>}
          <FormActions>
            <MainButton
              label='Cadastrar conta'
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || !banks}
              width={150}
            />
          </FormActions>
          <Divider/>
          <InfoText>
            Se preferir, mande o saldo pelo Pix para uma chave sua, sem cadastrar conta
          </InfoText>
        </Card>
      )}
    </Formik>
  )
}
